import React from 'react';
import { BookOpen, ClipboardCheck, FileBadge, UsersRound } from 'lucide-react';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, MetricCard, MiniBar, PageHeader, StatusBadge } from '../../components/ui';
import { assignmentGrade, derivedAssignmentStatus, fullName, getAssignmentProgress, getCourse, isOverdue } from '../../utils/lmsSelectors';

export default function AdminDashboard() {
  const { data } = useLms();
  const activeUsers = data.users.filter((user) => user.status === 'active').length;
  const publishedCourses = data.courses.filter((course) => course.status === 'published').length;
  const completedAssignments = data.assignments.filter((assignment) => assignment.status === 'completed').length;
  const overdue = data.assignments.filter(isOverdue).length;
  const completionRate = data.assignments.length ? Math.round((completedAssignments / data.assignments.length) * 100) : 0;
  const pendingCourses = data.courses.filter((course) => course.status === 'in_review' || course.status === 'changes_requested');
  const recentRows = data.assignments.slice(0, 6).map((assignment) => {
    const learner = data.users.find((user) => user.id === assignment.assignedToUserId);
    const course = getCourse(data, assignment.courseId);
    return {
      id: assignment.id,
      learner: fullName(learner),
      course: course?.title,
      status: derivedAssignmentStatus(data, assignment),
      progress: getAssignmentProgress(data, assignment).percent,
      grade: assignmentGrade(data, assignment)
    };
  });

  return (
    <>
      <PageHeader
        eyebrow="Admin Dashboard"
        title="Company LMS health"
        description="Monitor completion, overdue training, course approvals, grades, and certificates across all teams."
      />
      <div className="metrics-grid">
        <MetricCard label="Active users" value={activeUsers} helper="Admin, trainers, learners" icon={<UsersRound size={22} />} />
        <MetricCard label="Published courses" value={publishedCourses} helper="Assignable now" icon={<BookOpen size={22} />} />
        <MetricCard label="Completion rate" value={`${completionRate}%`} helper={`${completedAssignments}/${data.assignments.length} assignments`} icon={<ClipboardCheck size={22} />} />
        <MetricCard label="Overdue" value={overdue} helper="Past due and incomplete" icon={<FileBadge size={22} />} />
      </div>
      <div className="grid two">
        <Card>
          <h2>Training category mix</h2>
          {data.categories.map((category) => {
            const count = data.courses.filter((course) => course.category === category).length;
            const value = data.courses.length ? Math.round((count / data.courses.length) * 100) : 0;
            return <MiniBar key={category} label={`${category} (${count})`} value={value} />;
          })}
        </Card>
        <Card>
          <h2>Pending course governance</h2>
          {pendingCourses.length ? pendingCourses.map((course) => (
            <div key={course.id} className="stacked-row">
              <div><strong>{course.title}</strong><p className="muted small">{course.category}</p></div>
              <StatusBadge status={course.status} />
            </div>
          )) : <p className="muted">No courses need approval right now.</p>}
        </Card>
      </div>
      <Card>
        <h2>Assignment snapshot</h2>
        <DataTable
          rows={recentRows}
          columns={[
            { key: 'learner', label: 'Learner' },
            { key: 'course', label: 'Course' },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'progress', label: 'Progress', render: (row) => `${row.progress}%` },
            { key: 'grade', label: 'Grade', render: (row) => row.grade === null ? '—' : `${row.grade}%` }
          ]}
        />
      </Card>
    </>
  );
}
